import type { WorkQueueFilters, WorkQueueItem, WorkQueueItemStatus } from './myWorkQueueTypes';

/** Letters and digits only, upper-cased: "#ce-pilot-001" -> "CEPILOT001" */
const normalizeRequestNumber = (value: string) => value.replace(/[^a-z0-9]/gi, '').toUpperCase();

/**
 * Applies the search, customer, state and status filters, then sorts by date.
 * Pass ignoreStatus to get the set the status counters are based on.
 */
export function filterWorkQueueItems(
  items: WorkQueueItem[],
  filters: WorkQueueFilters,
  ignoreStatus = false
): WorkQueueItem[] {
  const requestQuery = normalizeRequestNumber(filters.requestNumber);
  const filtered = items.filter(
    (item) =>
      // Title too, so client-side matching agrees with the server-side data.title search
      (!requestQuery ||
        normalizeRequestNumber(item.request_number).includes(requestQuery) ||
        normalizeRequestNumber(item.title ?? '').includes(requestQuery)) &&
      (!filters.customer || item.customer === filters.customer) &&
      (!filters.state || item.state === filters.state) &&
      (ignoreStatus || !filters.status || item.status === filters.status)
  );
  const direction = filters.sort === 'newest' ? -1 : 1;
  return filtered.sort(
    (a, b) => direction * (new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
  );
}

export function countWorkQueueItemsByStatus(items: WorkQueueItem[]): Record<WorkQueueItemStatus, number> {
  return items.reduce<Record<WorkQueueItemStatus, number>>(
    (counts, item) => {
      counts[item.status] += 1;
      return counts;
    },
    { not_started: 0, in_progress: 0, completed: 0, cancelled: 0 }
  );
}

export function hasActiveWorkQueueFilters(filters: WorkQueueFilters): boolean {
  return Boolean(filters.requestNumber || filters.customer || filters.state || filters.status);
}

export function getUniqueSortedValues(items: WorkQueueItem[], key: 'customer' | 'state'): string[] {
  return Array.from(new Set(items.map((item) => item[key]).filter(Boolean))).sort((a, b) => a.localeCompare(b));
}

/** Formats as M/D/YYYY h:mm AM/PM, e.g. 7/17/2026 6:08 PM */
export function formatWorkQueueDate(isoDate: string): string {
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const period = hours < 12 ? 'AM' : 'PM';
  return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()} ${hours % 12 || 12}:${minutes} ${period}`;
}

/** "Lake Wales, FL 33859"; skips missing parts so API items without an address render cleanly */
export function formatWorkQueueLocation(item: Pick<WorkQueueItem, 'city' | 'state' | 'zip'>): string {
  const city = item.city ? formatWorkQueueCity(item.city) : '';
  const stateZip = [item.state, item.zip].filter(Boolean).join(' ');
  return [city, stateZip].filter(Boolean).join(', ');
}

/** Title-cases an upper-case city name, e.g. "DELRAY BEACH" → "Delray Beach" */
export function formatWorkQueueCity(value: string): string {
  return value.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
}
